import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, ArrowRight, CheckCircle2, Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { TrustBar } from "@/components/TrustBar";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { AREAS, getArea } from "@/data/areas";
import { SERVICES } from "@/data/services";
import { COMPANY } from "@/lib/constants";

const ICONS = { Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users };

export default function AreaDetail() {
  const { slug } = useParams();
  const area = getArea(slug);
  useSEO({
    title: area ? `Home Health Care in ${area.name}, VA | Esprit Home Healthcare` : "Service Area",
    description: area ? `Esprit Home Healthcare provides skilled nursing, therapy, and personal care in ${area.name}, ${area.county}. Trusted local home care. Call (703) 998-7400.` : "",
    canonical: `/service-areas/${slug}`,
  });

  if (!area) return <Navigate to="/service-areas" replace />;

  const nearby = AREAS.filter((a) => a.slug !== slug).slice(0, 6);

  const ld = {
    "@context": "https://schema.org",
    "@type": "HomeHealthCareService",
    name: `Esprit Home Healthcare — ${area.name}, VA`,
    telephone: "+1-703-998-7400",
    areaServed: { "@type": "City", name: `${area.name}, Virginia` },
    address: {
      "@type": "PostalAddress",
      streetAddress: "4209 Evergreen Lane, Suite 101",
      addressLocality: "Annandale",
      addressRegion: "VA",
      postalCode: "22003",
      addressCountry: "US",
    },
    description: `Skilled nursing, therapy, and personal care services delivered at home in ${area.name}, ${area.county}.`,
  };

  return (
    <div>
      <JsonLd id="area" data={ld} />

      <section className="bg-[hsl(var(--secondary))] text-white relative overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
        <div className="container-page py-10 lg:py-14 relative">
          <Breadcrumbs items={[{ label: "Service Areas", to: "/service-areas" }, { label: area.name }]} />
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-sm font-semibold mt-6">
            <MapPin className="w-4 h-4 text-accent" /> {area.county}
          </span>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 leading-tight">
            Home Health Care in <span className="text-accent">{area.name}, VA</span>
          </h1>
          <p className="text-lg text-white/75 mt-4 max-w-2xl leading-relaxed">
            Esprit Home Healthcare proudly serves {area.name} and the surrounding {area.county} community — {area.note}. From skilled nursing to personal care, we bring rehab-quality services right to your front door.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-7">
            <a href={COMPANY.phoneHref} data-testid="area-call-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 px-7 text-lg font-bold transition-colors">
              <Phone className="w-5 h-5" /> Call {COMPANY.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 hover:bg-white hover:text-secondary h-14 px-7 text-lg font-bold transition-colors">
              Free Consultation <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <TrustBar />

      <section className="container-page py-16 lg:py-20 grid lg:grid-cols-3 gap-12 items-start">
        <div className="lg:col-span-2 space-y-12">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-primary">Trusted Home Care for {area.name} Families</h2>
            <p className="text-lg text-foreground/80 mt-4 leading-relaxed">
              Choosing a home care provider in {area.name} is an important decision. Our local team understands the {area.county} community and delivers compassionate, professional care tailored to each patient. We are a preferred provider for most major insurance plans — and if we can't take it, we'll help place it.
            </p>
            <p className="text-lg text-foreground/80 mt-4 leading-relaxed">
              Whether your loved one is recovering from a hospital stay or needs ongoing daily support, one quick call connects you with skilled nurses, therapists, and caregivers serving {area.name} {area.zips?.length ? `(including ${area.zips.join(", ")})` : ""}.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-primary">Our Services in {area.name}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              {SERVICES.map((s) => {
                const Icon = ICONS[s.icon] || Stethoscope;
                return (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`area-service-${s.slug}`}
                    className="group flex items-start gap-3 bg-card rounded-2xl border border-border p-5 hover:border-primary transition-colors">
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="w-6 h-6" />
                    </span>
                    <span>
                      <span className="font-heading font-bold text-lg text-primary block">{s.shortName}</span>
                      <span className="text-sm text-muted-foreground">{s.tagline}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-[hsl(var(--muted))] rounded-[2rem] p-8">
              <h2 className="font-heading font-bold text-2xl text-primary">Why {area.name} families choose Esprit</h2>
              <ul className="grid sm:grid-cols-2 gap-3 mt-5">
                {["Local team that knows the area", "Preferred by most major insurers", "24/7 on-call availability", "Licensed, vetted caregivers", "Fast start — often within 24–48 hours", "One coordinated team for every need"].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-foreground/90">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="lg:sticky lg:top-28 space-y-6">
          <div className="bg-card rounded-[2rem] border border-border p-6">
            <h3 className="font-heading font-bold text-2xl text-primary mb-1">Start Care in {area.name}</h3>
            <p className="text-muted-foreground text-sm mb-4">Tell us about your needs and we'll reach out.</p>
            <LeadForm source={`area-${slug}`} compact />
          </div>

          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading font-bold text-xl text-primary mb-3">Nearby Areas We Serve</h3>
            <div className="flex flex-wrap gap-2">
              {nearby.map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-muted hover:bg-primary hover:text-primary-foreground px-3 py-1.5 text-sm transition-colors">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ConsultationCTA title={`Caring for ${area.name}, VA`} subtitle={`Speak with our local team about home health care in ${area.name}. We verify your insurance at no cost.`} />
    </div>
  );
}
